/**
 * NextActionService — V1035-4-2 / ADR-0071 §3.
 *
 * Given the program cards for a viewer, determines:
 *   1. Each program's next action (label + href).
 *   2. Which program gets primary={true} on Today (nearest hard date wins).
 *
 * Pure logic — no DB access. Consumes the output of `getProgramCards()`.
 */
const HARD_DATE_KINDS = new Set(['period_closes', 'target_date']);
function isHardDate(nd) {
    return HARD_DATE_KINDS.has(nd.kind);
}
function actionForProgram(card, slug) {
    const base = `/w/${slug}/app/programs/${card.program}`;
    const nd = card.next_date;
    const hardDate = nd && isHardDate(nd) ? nd.date : null;
    const softDate = nd && !isHardDate(nd) ? nd.date : null;
    if (card.blockers.overdue > 0) {
        return {
            program: card.program,
            profileId: card.profile_id,
            action: { label: `Fix ${card.blockers.overdue} overdue`, href: base },
            hardDate,
            softDate,
            reason: `${card.blockers.overdue} overdue item${card.blockers.overdue === 1 ? '' : 's'}`,
        };
    }
    if (card.status === 'off_track') {
        return {
            program: card.program,
            profileId: card.profile_id,
            action: { label: 'Review gaps', href: base },
            hardDate,
            softDate,
            reason: card.headline_reason?.text ?? 'Off track',
        };
    }
    if (card.blockers.soon > 0) {
        return {
            program: card.program,
            profileId: card.profile_id,
            action: { label: `${card.blockers.soon} due soon`, href: base },
            hardDate,
            softDate,
            reason: `${card.blockers.soon} item${card.blockers.soon === 1 ? '' : 's'} due soon`,
        };
    }
    if (nd) {
        return {
            program: card.program,
            profileId: card.profile_id,
            action: { label: nd.what, href: base },
            hardDate,
            softDate,
            reason: nd.what,
        };
    }
    return {
        program: card.program,
        profileId: card.profile_id,
        action: { label: 'View program', href: base },
        hardDate: null,
        softDate: null,
        reason: 'No upcoming dates',
    };
}
/**
 * Pick the primary program: the one whose hard date is nearest.
 * Ties break by manifest order (lower = earlier in the array = wins).
 * Programs with no hard date never win primary over one with a hard date.
 */
function pickPrimary(actions, manifestOrder) {
    if (actions.length === 0)
        return null;
    const withHard = actions.filter((a) => a.hardDate !== null);
    if (withHard.length === 0) {
        const withSoft = actions.filter((a) => a.softDate !== null);
        const pool = withSoft.length > 0 ? withSoft : actions;
        pool.sort((a, b) => {
            const da = a.softDate ?? '';
            const db = b.softDate ?? '';
            if (da !== db)
                return da < db ? -1 : 1;
            return manifestOrder.indexOf(a.program) - manifestOrder.indexOf(b.program);
        });
        return pool[0].program;
    }
    withHard.sort((a, b) => {
        if (a.hardDate !== b.hardDate)
            return a.hardDate < b.hardDate ? -1 : 1;
        return manifestOrder.indexOf(a.program) - manifestOrder.indexOf(b.program);
    });
    return withHard[0].program;
}
/**
 * Compute the next action for every active program card, and determine
 * which one gets primary on Today.
 */
export function computeTodayActions(cards, slug, manifestOrder) {
    const actions = cards.map((c) => actionForProgram(c, slug));
    const primaryProgram = pickPrimary(actions, manifestOrder);
    return { actions, primaryProgram };
}
//# sourceMappingURL=next-action-service.js.map